import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SlateDialog from 'components/Shared/SlateDialog/SlateDialog';
import { getImageLink, resizeImageToMaxEdge } from 'helpers/imageHelper';
import { imageSizes, SLATEMATE_IMAGE_MAX_EDGE_PX } from 'config/constants';
import {
  hydrateChatSession,
  reconcileSession,
  createSession,
  sendMessage,
  retryLastMessage,
  clearSession,
  addPendingAttachment,
  removePendingAttachment,
  clearPendingAttachments,
  abortActiveStream,
  cancelActiveTurn,
  subscribeStreamingDelta,
} from '../../store/actions/chatSession';
import './SlateMate.css';

var ACCEPTED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];
var MAX_PDF_FILE_SIZE = 10 * 1024 * 1024; // 10MB - PDFs are sent unchanged; images are downsampled instead
var CUMULATIVE_CAP = 10;
var PER_REQUEST_CAP = 3;

var PLACEHOLDER_COUNT = 10;

var SUGGESTION_KEYS = [
  { key: 'slateMateSuggestionConvertForm', hintKey: 'slateMateSuggestionConvertFormHint', icon: 'upload_file' },
  { key: 'slateMateSuggestionPrestartInspection', hintKey: 'slateMateSuggestionPrestartInspectionHint', icon: 'health_and_safety' },
  { key: 'slateMateSuggestionProductionStages', hintKey: 'slateMateSuggestionProductionStagesHint', icon: 'conveyor_belt' },
  { key: 'slateMateSuggestionRoutineWalkthrough', hintKey: 'slateMateSuggestionRoutineWalkthroughHint', icon: 'event_repeat' },
];

function fileToBase64(file) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.onload = function () {
      // result is "data:<mime>;base64,<data>" - extract just the base64 part
      var base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export var SlateMate = function (props) {
  var t = props.t;
  var chatSession = props.chatSession;
  var currentUser = props.currentUser;
  var imagesLinks = props.imagesLinks;

  var composeRef = useRef(null);
  var fileInputRef = useRef(null);
  var threadRef = useRef(null);
  var programmaticScrollRef = useRef(false);
  var programmaticScrollTimeoutRef = useRef(null);
  var placeholderIndexRef = useRef(Math.floor(Math.random() * PLACEHOLDER_COUNT) + 1);

  var _composeText = useState(function () {
    try {
      var saved = sessionStorage.getItem('slateMatePrompt');
      return saved || '';
    } catch (e) {
      return '';
    }
  });
  var composeText = _composeText[0];
  var setComposeText = _composeText[1];

  var _fileError = useState('');
  var fileError = _fileError[0];
  var setFileError = _fileError[1];

  var _dragover = useState(false);
  var dragover = _dragover[0];
  var setDragover = _dragover[1];

  var _startOverOpen = useState(false);
  var startOverOpen = _startOverOpen[0];
  var setStartOverOpen = _startOverOpen[1];

  var _activeHintKey = useState(null);
  var activeHintKey = _activeHintKey[0];
  var setActiveHintKey = _activeHintKey[1];

  var _stickToBottom = useState(true);
  var stickToBottom = _stickToBottom[0];
  var setStickToBottom = _stickToBottom[1];

  var _showNewMsgPill = useState(false);
  var showNewMsgPill = _showNewMsgPill[0];
  var setShowNewMsgPill = _showNewMsgPill[1];

  // Live streaming text buffer, keyed by assistant uuid. Populated via the
  // streaming-delta emitter so per-token updates don't churn Redux.
  var _streamingText = useState({});
  var streamingText = _streamingText[0];
  var setStreamingText = _streamingText[1];

  useEffect(function () {
    var unsub = subscribeStreamingDelta(function (evt) {
      if (evt.type === 'delta') {
        setStreamingText(function (prev) {
          var next = Object.assign({}, prev);
          next[evt.uuid] = (next[evt.uuid] || '') + evt.text;
          return next;
        });
      } else if (evt.type === 'paragraph_end' || evt.type === 'stream_end') {
        setStreamingText(function (prev) {
          var next = Object.assign({}, prev);
          delete next[evt.uuid];
          return next;
        });
      }
    });
    return unsub;
  }, []);

  // Hydrate from sessionStorage and reconcile with server on mount
  useEffect(function () {
    // The first render's props hold an empty chat state on a fresh page load, so reconcile the restored uuid.
    var restoredUuid = props.hydrateChatSession();
    if (restoredUuid) {
      props.reconcileSession(restoredUuid);
    }
    // Resize the textarea to fit any restored compose text
    autosizeCompose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist compose text across reloads
  useEffect(function () {
    try {
      if (composeText) {
        sessionStorage.setItem('slateMatePrompt', composeText);
      } else {
        sessionStorage.removeItem('slateMatePrompt');
      }
    } catch (e) { /* ignore */ }
  }, [composeText]);

  // Auto-scroll thread on updates
  useEffect(function () {
    if (!threadRef.current) return;
    if (stickToBottom) {
      programmaticScrollRef.current = true;
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
      if (programmaticScrollTimeoutRef.current) {
        clearTimeout(programmaticScrollTimeoutRef.current);
      }
      programmaticScrollTimeoutRef.current = setTimeout(function () {
        programmaticScrollRef.current = false;
      }, 600);
      setShowNewMsgPill(false);
    } else {
      setShowNewMsgPill(true);
    }
  }, [chatSession.messages.length, chatSession.toolStatuses, streamingText, stickToBottom]);

  function handleThreadScroll() {
    var el = threadRef.current;
    if (!el) return;
    if (programmaticScrollRef.current) return;
    var atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
    setStickToBottom(atBottom);
  }

  function autosizeCompose() {
    if (composeRef.current) {
      composeRef.current.style.height = 'auto';
      composeRef.current.style.height = composeRef.current.scrollHeight + 'px';
    }
  }

  function handleComposeChange(e) {
    setComposeText(e.target.value);
    autosizeCompose();
  }

  function handleComposeKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleSend() {
    var text = composeText.trim();
    if (!text || chatSession.streamStatus === 'streaming') return;
    var attachments = (chatSession.pendingAttachments || []).map(function (a) {
      return { fileName: a.fileName, mimeType: a.mimeType, content: a.base64 };
    });
    // [SM-DEBUG] what is being submitted
    console.log('[SM-DEBUG] handleSend', {
      textLength: text.length,
      attachmentCount: attachments.length,
      attachments: attachments.map(function (a) {
        return {
          fileName: a.fileName,
          mimeType: a.mimeType,
          contentLength: a.content ? a.content.length : 0,
        };
      }),
    });
    props.sendMessage(text, attachments);
    setComposeText('');
    if (composeRef.current) composeRef.current.style.height = 'auto';
    setStickToBottom(true);
  }

  function handleCancel() {
    props.abortActiveStream();
  }

  function handleRetry() {
    props.retryLastMessage();
  }

  function handleStartOver() {
    setStartOverOpen(false);
    props.clearSession();
    setComposeText('');
    if (composeRef.current) composeRef.current.style.height = 'auto';
  }

  function applySuggestion(text) {
    setComposeText(text);
    setTimeout(function () {
      autosizeCompose();
      if (composeRef.current) composeRef.current.focus();
    }, 0);
  }

  function openFilePicker() {
    if (fileInputRef.current) fileInputRef.current.click();
  }

  function handleFileInputChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      validateAndAddFiles(Array.prototype.slice.call(e.target.files));
    }
    e.target.value = '';
  }

  function validateAndAddFiles(newFiles) {
    setFileError('');
    var pending = chatSession.pendingAttachments || [];
    var serverDocs = chatSession.serverDocumentCount || 0;

    for (var i = 0; i < newFiles.length; i++) {
      var file = newFiles[i];
      if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
        setFileError(t('slateGenerator:slateMateInvalidFileType'));
        return;
      }
      if (file.type === 'application/pdf' && file.size > MAX_PDF_FILE_SIZE) {
        setFileError(t('slateGenerator:slateMateMaxFileSize'));
        return;
      }
    }

    if (pending.length + newFiles.length > PER_REQUEST_CAP) {
      setFileError(t('slateGenerator:slateMateAttachmentPerRequestCap'));
      return;
    }
    if (serverDocs + pending.length + newFiles.length > CUMULATIVE_CAP) {
      setFileError(t('slateGenerator:slateMateDocumentCapExceeded'));
      return;
    }

    // Encode each file and dispatch. Image files are downscaled so their
    // longest edge fits the model's native resolution (SLATEMATE_IMAGE_MAX_EDGE_PX)
    // before encoding -- this keeps us under Anthropic's hard 8000px dimension
    // limit and 5MB size limit, and cuts upload size with no fidelity loss
    // (the API would downscale to the same size server-side anyway). Images
    // beyond 100 MP are rejected; PDFs and already-small, already-upright
    // images pass through unchanged.
    var addPromise = Promise.resolve();
    var _loop = function (file) {
      addPromise = addPromise.then(function () {
        // [SM-DEBUG] resize input
        console.log('[SM-DEBUG] prepare file', {
          name: file.name,
          type: file.type,
          size: file.size,
          maxEdge: SLATEMATE_IMAGE_MAX_EDGE_PX,
        });
        var prepare;
        if (file.type.indexOf('image/') === 0) {
          prepare = resizeImageToMaxEdge(file, {
            maxEdge: SLATEMATE_IMAGE_MAX_EDGE_PX,
            quality: 0.85,
          }).then(
            function (blob) {
              // [SM-DEBUG] resize success
              console.log('[SM-DEBUG] resize ok', {
                name: file.name,
                originalSize: file.size,
                resizedSize: blob ? blob.size : null,
                resizedType: blob ? blob.type : null,
                sameAsOriginal: blob === file,
              });
              return { ok: true, file: blob };
            },
            function (err) {
              // [SM-DEBUG] resize failure
              console.log('[SM-DEBUG] resize FAILED', {
                name: file.name,
                code: err && err.code,
                message: err && err.message,
              });
              if (err && err.code === 'IMAGE_TOO_LARGE') {
                // Beyond what we will process -- surface a message and skip
                // it rather than sending a too-large original.
                return { ok: false };
              }
              // Any other resize failure: fall back to the original file so
              // the attachment still goes through.
              return { ok: true, file: file };
            }
          );
        } else {
          prepare = Promise.resolve({ ok: true, file: file });
        }
        return prepare.then(function (outcome) {
          if (!outcome.ok) {
            setFileError(t('slateGenerator:slateMateImageTooLarge'));
            return;
          }
          var prepared = outcome.file;
          return fileToBase64(prepared).then(function (base64) {
            // [SM-DEBUG] encoded
            console.log('[SM-DEBUG] base64 encoded', {
              name: file.name,
              mimeType: prepared.type ? prepared.type : file.type,
              base64Length: base64 ? base64.length : 0,
            });
            var added = props.addPendingAttachment({
              fileName: file.name,
              mimeType: prepared.type ? prepared.type : file.type,
              base64: base64,
            });
            // [SM-DEBUG] addPendingAttachment result
            console.log('[SM-DEBUG] addPendingAttachment', { name: file.name, added: added });
            if (!added) {
              setFileError(t('slateGenerator:slateMateAttachmentPerRequestCap'));
            }
          });
        }).catch(function (e) {
          // [SM-DEBUG] per-file catch
          console.log('[SM-DEBUG] per-file catch', { name: file.name, error: e && e.message, stack: e && e.stack });
          // A FileReader/encode failure on one file must not surface as an
          // unhandled rejection or break the rest of the batch. Because the
          // chain is sequential (addPromise = addPromise.then(...)), an
          // unhandled rejection here would also silently drop every later
          // file. Swallow per-file and show a generic error.
          setFileError(t('slateGenerator:slateMateFileReadFailed'));
        });
      });
    };
    for (var j = 0; j < newFiles.length; j++) {
      _loop(newFiles[j]);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragover(true);
  }
  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragover(false);
  }
  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragover(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndAddFiles(Array.prototype.slice.call(e.dataTransfer.files));
    }
  }

  var messages = chatSession.messages || [];
  var pendingAttachments = chatSession.pendingAttachments || [];
  var serverDocs = chatSession.serverDocumentCount || 0;
  var totalAttachments = serverDocs + pendingAttachments.length;
  var streaming = chatSession.streamStatus === 'streaming';
  var toolStatuses = chatSession.toolStatuses || [];
  var takingAWhile = chatSession.takingAWhile;
  var thinkingLabel = chatSession.thinkingLabel || null;
  var recovering = chatSession.recovering === true;
  var conversationActive = messages.length > 0;

  var userAvatarUrl = null;
  if (currentUser && currentUser.imageUUID) {
    userAvatarUrl = getImageLink(
      currentUser.imageUUID,
      imagesLinks,
      imageSizes.USER_TOPBAR
    );
  }
  var userInitial = currentUser && currentUser.profileLetter
    ? currentUser.profileLetter
    : (currentUser && currentUser.name ? currentUser.name[0].toUpperCase() : '');

  function renderUserAvatar() {
    return (
      <div className="slatemate-avatar slatemate-avatar--user" aria-hidden="true">
        {userAvatarUrl
          ? <img src={userAvatarUrl} alt="" />
          : userInitial}
      </div>
    );
  }

  function renderBotAvatar() {
    return (
      <div className="slatemate-avatar slatemate-avatar--bot" aria-hidden="true">
        <img src="/images/SlatePages-S.svg" alt="Slate Pages" />
      </div>
    );
  }

  function renderEmptyState() {
    return (
      <div className="slatemate-empty-state">
        <p className="slatemate-welcome-subtitle">
          <span>{t('slateGenerator:slateMateWelcomeSubtitlePrimary')}</span>
          <span>{t('slateGenerator:slateMateWelcomeSubtitleSecondary')}</span>
        </p>
        <div className="slatemate-suggestions">
          {SUGGESTION_KEYS.map(function (s) {
            var label = t('slateGenerator:' + s.key);
            var hint = s.hintKey ? t('slateGenerator:' + s.hintKey) : '';
            var isActive = activeHintKey === s.key;
            var hintId = 'slatemate-hint-' + s.key;
            return (
              <button
                key={s.key}
                type="button"
                className={'slatemate-suggestion' + (isActive ? ' slatemate-suggestion--hint-active' : '')}
                onClick={function () { applySuggestion(label); }}
              >
                <span className="material-symbols-outlined slatemate-suggestion-icon">{s.icon}</span>
                <span className="slatemate-suggestion-label">{label}</span>
                {hint ? (
                  <span
                    className="slatemate-suggestion-info"
                    aria-describedby={hintId}
                    onMouseEnter={function () { setActiveHintKey(s.key); }}
                    onMouseLeave={function () { setActiveHintKey(null); }}
                    onClick={function (e) {
                      e.stopPropagation();
                      setActiveHintKey(isActive ? null : s.key);
                    }}
                    onMouseDown={function (e) { e.stopPropagation(); }}
                  >
                    <span className="material-symbols-outlined">info</span>
                  </span>
                ) : null}
                {hint ? (
                  <span
                    id={hintId}
                    role="tooltip"
                    className="slatemate-suggestion-hint"
                    aria-hidden={!isActive}
                  >
                    {hint}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function renderThread() {
    return (
      <div className="slatemate-thread" ref={threadRef} onScroll={handleThreadScroll}>
        {messages.map(function (m, i) {
          var rowKey = m.uuid ? m.uuid : 'idx-' + i;
          if (m.role === 'user') {
            return (
              <div key={rowKey} className="slatemate-msg slatemate-msg-user">
                {renderUserAvatar()}
                <div className="slatemate-msg-user-bubble">
                  {m.text}
                  {m.attachments && m.attachments.length > 0 ? (
                    <div className="slatemate-msg-attachments">
                      {m.attachments.map(function (a, j) {
                        return (
                          <span key={j} className="slatemate-msg-attachment-chip">
                            <span className="material-symbols-outlined">description</span>
                            {a.fileName}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          }
          var isStreaming = m.status === 'streaming';
          var isInterrupted = m.status === 'interrupted';
          var isErrored = m.status === 'errored';
          var liveDelta = m.uuid ? (streamingText[m.uuid] || '') : '';
          var combined = (m.text || '') + liveDelta;
          var awaitingFirstToken = isStreaming && combined.length === 0;
          return (
            <div key={rowKey} className="slatemate-msg slatemate-msg-assistant">
              {renderBotAvatar()}
              <div className="slatemate-msg-assistant-content">
                <div className={'slatemate-msg-body' + (isStreaming && combined.length > 0 ? ' slatemate-msg-cursor' : '')}>
                  {awaitingFirstToken ? (
                    <span className="slatemate-thinking" aria-label={t('slateGenerator:slateMateThinking')}>
                      <span /><span /><span />
                    </span>
                  ) : (
                    <ReactMarkdown
                      className="slatemate-markdown"
                      remarkPlugins={[remarkGfm]}
                      linkTarget="_blank"
                    >
                      {combined}
                    </ReactMarkdown>
                  )}
                </div>
                {(isInterrupted || isErrored) ? (
                  <div className="slatemate-msg-status">
                    <span className="slatemate-msg-status-label">
                      {translateAssistantStatus(m, t)}
                    </span>
                    {m.errorCode === 'still_working' ? null : (
                      <button
                        type="button"
                        className="slatemate-msg-retry"
                        onClick={handleRetry}
                        disabled={streaming}
                      >
                        <span className="material-symbols-outlined">refresh</span>
                        {t('slateGenerator:slateMateRetry')}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
        <div className="slatemate-status-tray">
          {recovering ? (
            <div className="slatemate-tool-status slatemate-recovering">
              <span className="slatemate-spinner" />
              {t('slateGenerator:slateMateRecovering')}
            </div>
          ) : null}
          {(recovering ? [] : toolStatuses).map(function (ts, ti) {
            var isLast = ti === toolStatuses.length - 1;
            return (
              <div key={ts.toolUseId} className="slatemate-tool-status">
                <span className="slatemate-spinner" />
                {ts.message}
                {takingAWhile && isLast ? (
                  <span className="slatemate-tool-status-suffix">
                    {t('slateGenerator:slateMateStillWorking')}
                  </span>
                ) : null}
              </div>
            );
          })}
          {streaming && !recovering && thinkingLabel ? (
            <div className="slatemate-tool-status slatemate-thinking-status">
              <span className="slatemate-spinner" />
              {thinkingLabel}
            </div>
          ) : null}
          {streaming && !recovering && toolStatuses.length === 0 && !thinkingLabel ? (
            <div className="slatemate-tool-status">
              <span className="slatemate-spinner" />
            </div>
          ) : null}
        </div>
        {showNewMsgPill && !stickToBottom ? (
          <button
            type="button"
            className="slatemate-new-messages-pill"
            onClick={function () { setStickToBottom(true); }}
          >
            <span className="material-symbols-outlined">arrow_downward</span>
            {t('slateGenerator:slateMateNewMessages')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="slatemate">
      <div
        className={
          'slatemate-form'
          + (dragover ? ' slatemate-form--dragover' : '')
          + (conversationActive ? ' slatemate-form--active' : ' slatemate-form--empty')
        }
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {conversationActive ? renderThread() : renderEmptyState()}

        <div className="slatemate-compose-wrap">
          <textarea
            ref={composeRef}
            className="slatemate-compose"
            value={composeText}
            onChange={handleComposeChange}
            onKeyDown={handleComposeKeyDown}
            placeholder={conversationActive
              ? t('slateGenerator:slateMatePromptPlaceholderActive')
              : t('slateGenerator:slateMatePromptPlaceholder' + placeholderIndexRef.current)}
            disabled={streaming}
          />

          {pendingAttachments.length > 0 ? (
            <div className="slatemate-files">
              <div className="slatemate-file-chips">
                {pendingAttachments.map(function (a, i) {
                  return (
                    <div key={i} className="slatemate-file-chip">
                      <span className="material-symbols-outlined">description</span>
                      <span className="slatemate-file-chip-name">{a.fileName}</span>
                      <button
                        type="button"
                        className="slatemate-file-chip-remove"
                        onClick={function () { props.removePendingAttachment(i); }}
                        disabled={streaming}
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {fileError ? <div className="slatemate-file-error">{fileError}</div> : null}

          <div className="slatemate-toolbar">
            <button
              type="button"
              className="slatemate-icon-btn"
              onClick={openFilePicker}
              disabled={streaming || totalAttachments >= CUMULATIVE_CAP}
              title={t('slateGenerator:slateMateAttachFiles')}
            >
              <span className="material-symbols-outlined">attach_file</span>
            </button>
            {totalAttachments > 0 ? (
              <span className="slatemate-attach-counter">
                {t('slateGenerator:slateMateAttachmentCounter', { count: totalAttachments })}
              </span>
            ) : null}
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />

            <div className="slatemate-toolbar-spacer" />

            {conversationActive ? (
              <button
                type="button"
                className="slatemate-start-over-btn"
                onClick={function () { setStartOverOpen(true); }}
                disabled={streaming}
              >
                {t('slateGenerator:slateMateStartOver')}
              </button>
            ) : null}

            {streaming ? (
              <button
                type="button"
                className="slatemate-cancel-btn"
                onClick={handleCancel}
                title={t('slateGenerator:slateMateCancel')}
                aria-label={t('slateGenerator:slateMateCancel')}
              >
                <span className="slatemate-cancel-btn-spinner" aria-hidden="true" />
                <span className="slatemate-cancel-btn-stop material-symbols-outlined" aria-hidden="true">stop</span>
              </button>
            ) : (
              <button
                type="button"
                className="slatemate-send-btn"
                onClick={handleSend}
                disabled={!composeText.trim()}
                aria-label={conversationActive
                  ? t('slateGenerator:slateMateSend')
                  : t('slateGenerator:slateMateGenerate')}
              >
                <span className="material-symbols-outlined">
                  {conversationActive ? 'arrow_upward' : 'auto_awesome'}
                </span>
                {conversationActive
                  ? t('slateGenerator:slateMateSend')
                  : t('slateGenerator:slateMateGenerate')}
              </button>
            )}
          </div>
        </div>
      </div>

      {chatSession.streamError ? (
        <div className="slatemate-api-error">
          {translateStreamError(chatSession.streamError, t)}
        </div>
      ) : null}

      <SlateDialog
        open={startOverOpen}
        onClose={function () { setStartOverOpen(false); }}
        title={t('slateGenerator:slateMateStartOverChatTitle')}
        maxWidth="xs"
        actions={[
          {
            label: t('btn:cancel'),
            onClick: function () { setStartOverOpen(false); },
            variant: 'secondary',
          },
          {
            label: t('slateGenerator:slateMateStartOver'),
            onClick: handleStartOver,
            variant: 'destructive',
          },
        ]}
      >
        <p className="slatemate-start-over-message">{t('slateGenerator:slateMateStartOverChatMessage')}</p>
      </SlateDialog>
    </div>
  );
};

function translateAssistantStatus(message, t) {
  if (message.status === 'interrupted') return t('slateGenerator:slateMateInterruptedMessage');
  if (message.errorCode === 'loop_limit') return t('slateGenerator:slateMateLoopLimitMessage');
  if (message.errorCode === 'coordinator_failed') return t('slateGenerator:slateMateCoordinatorFailedMessage');
  if (message.errorCode === 'invalid_slate') return t('slateGenerator:slateMateInvalidSlateMessage');
  if (message.errorCode === 'session_busy') return t('slateGenerator:slateMateSessionBusy');
  if (message.errorCode === 'document_cap_exceeded') return t('slateGenerator:slateMateDocumentCapExceeded');
  if (message.errorCode === 'validation') return t('slateGenerator:slateMateValidationError');
  if (message.errorCode === 'timeout') return t('slateGenerator:slateMateTimeoutError');
  if (message.errorCode === 'still_working') return t('slateGenerator:slateMateStillBuilding');
  return t('slateGenerator:slateMateGenericNetworkError');
}

function translateStreamError(err, t) {
  if (!err) return '';
  if (err.code === 'invalid_slate') return t('slateGenerator:slateMateInvalidSlateMessage');
  if (err.code === 'session_busy') return t('slateGenerator:slateMateSessionBusy');
  if (err.code === 'document_cap_exceeded') return t('slateGenerator:slateMateDocumentCapExceeded');
  if (err.code === 'validation') return t('slateGenerator:slateMateValidationError');
  if (err.code === 'timeout') return t('slateGenerator:slateMateTimeoutError');
  return t('slateGenerator:slateMateGenericNetworkError');
}

export function clearSlateMateSessionStorage() {
  cancelActiveTurn();
  try {
    // Legacy keys (will not exist after migration; cleared defensively)
    sessionStorage.removeItem('slateMatePrompt');
    sessionStorage.removeItem('slateMateFiles');
    sessionStorage.removeItem('slateMateFilesLost');
    // New chat keys
    sessionStorage.removeItem('slateMateChatSessionUuid');
    sessionStorage.removeItem('slateMateChatMessages');
    sessionStorage.removeItem('slateMateChatAttachments');
  } catch (e) { /* ignore */ }
}

function mapStateToProps(state) {
  return {
    chatSession: state.chatSession,
    contentModified: state.slatePane.contentModified,
    isPaneOpen: state.slatePane.isPaneOpen,
    currentUser: state.currentUser,
    imagesLinks: state.images && state.images.links,
  };
}

var mapDispatchToProps = {
  hydrateChatSession: hydrateChatSession,
  reconcileSession: reconcileSession,
  createSession: createSession,
  sendMessage: sendMessage,
  retryLastMessage: retryLastMessage,
  clearSession: clearSession,
  addPendingAttachment: addPendingAttachment,
  removePendingAttachment: removePendingAttachment,
  clearPendingAttachments: clearPendingAttachments,
  abortActiveStream: abortActiveStream,
};

export default connect(mapStateToProps, mapDispatchToProps)(SlateMate);
